/**
 * AP-34 11.5, Nachtrag 1 8.3 — Zähler **„Meine Freigaben"** am Menüpunkt: wie viele
 * Eingangsrechnungen auf die Freigabe des angemeldeten Benutzers warten.
 *
 * Geladen wird beim Start, alle 60 Sekunden, wenn das Fenster den Fokus bekommt — und sofort,
 * wenn eine Seite `freigabenZahlNeu()` ruft (nach Abschließen und nach einer Entscheidung).
 */
import { useEffect, useSyncExternalStore } from 'react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { api } from '@/lib/axios'

let zahl = 0
const hoerer = new Set<() => void>()
const setze = (n: number) => {
  if (n === zahl) return
  zahl = n
  hoerer.forEach((h) => h())
}

const darf = () => {
  const { user, hasModule } = useAuthStore.getState()
  return !!user && hasModule('INCOMING_INVOICES.CORE') && erfuelltRecht('incoming_invoices.approval.decide', user.permissions)
}

export async function freigabenZahlNeu(): Promise<void> {
  if (!darf()) return setze(0)
  try {
    const r = await api.get<{ data: { count: number } }>('/incoming-invoices/approvals/count')
    setze(r.data.data.count)
  } catch {
    // Der Zähler ist Bequemlichkeit — ein Fehler hier stört die Seite nicht.
  }
}

/** Einmal im Layout. */
export function useFreigabenZaehler() {
  const user = useAuthStore((s) => s.user)
  useEffect(() => {
    void freigabenZahlNeu()
    const uhr = window.setInterval(() => void freigabenZahlNeu(), 60_000)
    const fokus = () => void freigabenZahlNeu()
    window.addEventListener('focus', fokus)
    return () => {
      window.clearInterval(uhr)
      window.removeEventListener('focus', fokus)
    }
  }, [user])
}

/** Die Zahl als Plakette — nichts bei 0. */
export function FreigabenZahl() {
  const n = useSyncExternalStore(
    (h) => {
      hoerer.add(h)
      return () => hoerer.delete(h)
    },
    () => zahl,
  )
  if (!n) return null
  const titel = n === 1 ? '1 Rechnung wartet auf deine Freigabe' : `${n} Rechnungen warten auf deine Freigabe`
  return (
    <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold tabular-nums text-destructive-foreground" title={titel} aria-label={titel}>
      {n}
    </span>
  )
}
